<script setup lang="ts">
import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons';
import { NuxtLink } from '#components';
import { type QuickAccess } from '@store/content/entities/QuickAccess';

interface IProps {
    title: string;
    description: string;
    icon: QuickAccess['Icon'];
    color: string;
    link?: string;
    element?: HTMLElement;
}
const {
    link = '',
    element,
    description,
    icon,
    color,
    title,
} = defineProps<IProps>();

const emit = defineEmits<{
    click: [event: MouseEvent];
}>();

const component = computed(() => (link ? NuxtLink : 'div'));
const componentProps = computed(() => {
    if (link) {
        return {
            to: link,
        };
    } else {
        return {};
    }
});
const componentEvents = computed(() => {
    if (link) {
        return {};
    } else if (element) {
        return {
            click: () => element?.scrollIntoView({ behavior: 'smooth' }),
        };
    } else {
        return {
            click: (event: MouseEvent) => emit('click', event),
        };
    }
});
</script>
<template>
    <component
        :is="component"
        class="root-container"
        :class="{ 'link-item': link }"
        v-bind="componentProps"
        v-on="componentEvents"
    >
        <div class="icon-container" :style="{ '--color': color }">
            <img :src="icon.src" :alt="icon.alt" class="icon" />
        </div>
        <div class="body">
            <div class="title-container">
                <mr-typography
                    weight="700"
                    color="var(--blue-400)"
                    class="title"
                    white-space="nowrap"
                >
                    {{ title }}
                </mr-typography>
                <mr-icon
                    :icon="faAngleLeft"
                    color="var(--blue-400)"
                    size="small"
                />
            </div>
            <mr-typography
                weight="500"
                color="var(--gray-700)"
                :white-space="{ desktop: 'unset', mobile: 'nowrap' }"
            >
                {{ description }}
            </mr-typography>
        </div>
    </component>
</template>

<style lang="scss" scoped>
.link-item {
    cursor: pointer !important;
}
.root-container {
    flex: 1;
    display: flex;
    align-items: center;
    padding: 8px 8px;
    text-decoration: none;
    cursor: default;
    &:hover {
        text-decoration: none;
    }
    @include f-gap(12px, $offset-start: 12px);
    .icon-container {
        background-color: var(--color);
        width: 52px;
        height: 52px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 12px;
        .icon {
            width: 20px !important;
        }
    }
    .body {
        display: flex;
        flex-direction: column;
        .title-container {
            display: flex;
            align-items: center;
            @include f-gap(10px);
        }
    }
}
</style>
<style lang="scss">
html.smooth-scroll {
    scroll-behavior: smooth;
    @include mobile-only {
        scroll-padding-top: 48px;
    }
}
</style>
